import { Button, Row, Space, Modal, Form } from 'antd'
import moment from 'moment'
import Search from '@/components/SearchFilter'
import TableAntd from '@/components/TableAntd'
import Buttonbar from '@/components/ButtonBar'
import Uploadfiles from '@/modules/uploadfiles'
import { fileFile } from '@/modules/uploadfiles/hooks'
import {
  agreementSigningTypeEnum,
  agreementSignStatusEnum,
  agreementStatusEnum,
  agreementFileReviewStateEnum,
  AgreementSigningType,
  AgreementSignStatus,
  AgreementStatus,
  AgreementFileReviewState,
} from '@/global/enum/selfAgreement'
import { FrontSelfContractListVO } from '@/api/selfAgreementApi/response'
import { cn } from '@xhgj/xh-jsutils'
import { useSetupHook } from './hooks'
import styles from './styles.module.scss'

const UPLOAD_BTN_STYLE = {
  border: 'none',
  fontSize: '12px',
  color: '#3B44D3',
  backgroundColor: '#fff',
}
const UPLOAD_ACCEPT = '.pdf,.png,.jpg,.jpeg,.bmp,.gif'
// 审核详情弹窗「重新上传」按钮样式（复用 Uploadfiles 原生上传按钮）
const REUPLOAD_BTN_STYLE = {
  margin: 0,
  border: 'none',
  color: '#fff',
  backgroundColor: '#3B44D3',
}
const REVIEWING_TIP = '已提交H0审批，等待协议创建人审核。'

type TagColor = 'green' | 'gray' | 'yellow' | 'red' | 'blue'

// 通用状态标签（协议签署状态 / 协议状态）
const renderStatusTag = (text: string | undefined, color: TagColor) =>
  text ? (
    <span className={cn(styles['tag'], styles[color])}>{text}</span>
  ) : (
    <>-</>
  )

// 协议有效期（毫秒时间戳）→ 展示文本
const formatValidity = (record: FrontSelfContractListVO): string => {
  const { effectiveStart, effectiveEnd } = record
  if (!effectiveStart && !effectiveEnd) {
    return '-'
  }
  const start = effectiveStart
    ? moment(effectiveStart).format('YYYY-MM-DD')
    : ''
  const end = effectiveEnd ? moment(effectiveEnd).format('YYYY-MM-DD') : ''
  return `${start} ~ ${end}`
}

// 协议签署状态 → 标签颜色（未列出的默认灰）
const SIGN_STATUS_COLOR: Record<string, TagColor> = {
  双方已签署: 'green',
  我方已签署: 'yellow',
}
// 协议状态 → 标签颜色（未列出的默认灰）
const AGREEMENT_STATUS_COLOR: Record<string, TagColor> = {
  生效: 'green',
  失效: 'red',
}

export default function SelfAgreement() {
  const {
    state,
    searchRef,
    handlePaginationChange,
    handleTableChangeCheck,
    handleSearchClick,
    downloadFile,
    getAttachment,
    inTableUploadfile,
    fileListChange,
    cancelUpload,
    handleDownloadPendingFile,
  } = useSetupHook()
  const {
    dataSource,
    isTableLoading,
    paginationOption,
    downloadLoading,
    showList,
    fileList,
    fileStatus,
    reason,
    agreementId,
  } = state

  const reviewState = fileStatus?.fileReviewState
  const isReviewing = agreementFileReviewStateEnum.isValueInLabels(
    reviewState,
    ['审核中'],
  )
  const isRejected = agreementFileReviewStateEnum.isValueInLabels(reviewState, [
    '审核驳回',
  ])
  // 审核状态只读文案（label 即展示文案，直接取 enum）
  const statusText =
    agreementFileReviewStateEnum.getLabelByValue(
      reviewState as AgreementFileReviewState,
    ) ?? '-'

  // 协议待签署文件列：字段有值才展示按钮，文案按签订方式区分
  const renderPendingSignCell = (
    text: string,
    record: FrontSelfContractListVO,
  ) => {
    if (!text) {
      return '-'
    }
    // 全电子协议：跳转签署链接
    if (
      agreementSigningTypeEnum.isValueInLabels(record.signingType, [
        '全电子协议',
      ])
    ) {
      return (
        <Button type="link" onClick={() => window.open(text)}>
          签署链接
        </Button>
      )
    }
    // 我方电子对方纸质：拉取 contractPendingAttachment 链接内容并下载
    if (
      agreementSigningTypeEnum.isValueInLabels(record.signingType, [
        '我方电子对方纸质',
      ])
    ) {
      return (
        <Button
          type="link"
          onClick={() => handleDownloadPendingFile(text)}
        >
          下载待签署链接
        </Button>
      )
    }
    return '-'
  }

  // 协议附件列
  const renderAttachmentCell = (record: FrontSelfContractListVO) => {
    // 审核通过：展示归档附件文件名，预览/下载
    if (
      agreementFileReviewStateEnum.isValueInLabels(record.fileReviewState, [
        '审核通过',
      ])
    ) {
      const fileList = record.archiveFile
        ? [record.archiveFile as fileFile]
        : []
      return (
        <Uploadfiles
          dataSource={fileList}
          maxCount={1}
          type="contract"
          code="file"
          NoUploadButton
          disabledDel
        />
      )
    }
    // 审核中 / 审核驳回：标签，点击弹窗
    if (
      agreementFileReviewStateEnum.isValueInLabels(record.fileReviewState, [
        '审核中',
        '审核驳回',
      ])
    ) {
      const isRejectedRow = agreementFileReviewStateEnum.isValueInLabels(
        record.fileReviewState,
        ['审核驳回'],
      )
      return (
        <span
          className={cn(
            styles['tag'],
            styles['clickable'],
            isRejectedRow ? styles['red'] : styles['yellow'],
          )}
          onClick={() => getAttachment(record)}
        >
          {agreementFileReviewStateEnum.getLabelByValue(
            record.fileReviewState as AgreementFileReviewState,
          )}
        </span>
      )
    }
    // 未上传：显隐以后端 uploadSupported 为准
    if (record.uploadSupported) {
      return (
        <Uploadfiles
          title="上传"
          dataSource={[]}
          onChange={(e: fileFile[]) => inTableUploadfile({ e, record })}
          accept={UPLOAD_ACCEPT}
          maxCount={1}
          type="contract"
          code={record.id ?? ''}
          uploadBtnStyle={UPLOAD_BTN_STYLE}
          disabledDel
        />
      )
    }
    return '-'
  }

  const columns = [
    {
      key: 'contractNo',
      dataIndex: 'contractNo',
      title: '协议编号',
      width: 150,
    },
    {
      key: 'firstSigningGroupName',
      dataIndex: 'firstSigningGroupName',
      title: '甲方',
      width: 180,
    },
    {
      key: 'secondSigningSupplierName',
      dataIndex: 'secondSigningSupplierName',
      title: '乙方',
      width: 180,
    },
    {
      key: 'signingType',
      dataIndex: 'signingType',
      title: '签订方式',
      width: 130,
      render: (text: string) =>
        agreementSigningTypeEnum.getLabelByValue(
          text as AgreementSigningType,
        ) ??
        text ??
        '-',
    },
    {
      key: 'validity',
      dataIndex: 'effectiveStart',
      title: '协议有效期',
      width: 185,
      render: (text: number, record: FrontSelfContractListVO) =>
        formatValidity(record),
    },
    {
      key: 'untilContractExpirationDays',
      dataIndex: 'untilContractExpirationDays',
      title: '距离到期天数',
      width: 110,
    },
    {
      key: 'signatureStatus',
      dataIndex: 'signatureStatus',
      title: '协议签署状态',
      width: 120,
      render: (text: string) => {
        const label =
          agreementSignStatusEnum.getLabelByValue(
            text as AgreementSignStatus,
          ) ?? text
        return renderStatusTag(label, SIGN_STATUS_COLOR[label] ?? 'gray')
      },
    },
    {
      key: 'contractStatus',
      dataIndex: 'contractStatus',
      title: '协议状态',
      width: 90,
      render: (text: string) => {
        const label =
          agreementStatusEnum.getLabelByValue(text as AgreementStatus) ?? text
        return renderStatusTag(label, AGREEMENT_STATUS_COLOR[label] ?? 'gray')
      },
    },
    {
      key: 'contractPendingAttachment',
      dataIndex: 'contractPendingAttachment',
      title: '协议待签署文件',
      align: 'left',
      width: 120,
      render: renderPendingSignCell,
    },
    {
      key: 'archiveFile',
      dataIndex: 'archiveFile',
      title: '协议附件',
      width: 170,
      render: (text: unknown, record: FrontSelfContractListVO) =>
        renderAttachmentCell(record),
    },
  ]
  return (
    <div className={styles['container']}>
      <Search
        fieldsData={[
          'contractNo',
          'firstSigningGroupName',
          'agreementSigningType',
          'agreementSignStatus',
          'agreementStatus',
          'effectiveStart',
          'effectiveEnd',
          'untilContractExpirationDays',
        ]}
        onSearch={handleSearchClick}
        keyName="SELFAGREEMENT"
        ScreenKey="SELFAGREEMENT"
        currentRef={searchRef}
      />
      <Row justify="space-between" className={styles['info-row']}>
        <Space>
          <Buttonbar onRefresh={() => handlePaginationChange(1)}></Buttonbar>
        </Space>
        <Space>
          <Button
            size="middle"
            type="primary"
            loading={downloadLoading}
            onClick={downloadFile}
          >
            下载协议附件
          </Button>
        </Space>
      </Row>
      <TableAntd
        dataSource={dataSource}
        listColumn={columns as any[]}
        loading={isTableLoading}
        Y={340}
        X={1445}
        pagination={{
          ...paginationOption,
          onChange: handlePaginationChange,
        }}
        showSize
        onChangeCheck={handleTableChangeCheck}
      ></TableAntd>
      <Modal
        title="附件审核详情"
        open={showList}
        destroyOnClose
        onCancel={cancelUpload}
        footer={
          <Row justify="end">
            <Space>
              <Button onClick={cancelUpload}>关闭</Button>
              {isRejected && (
                <Uploadfiles
                  title="重新上传"
                  dataSource={[]}
                  onChange={fileListChange}
                  accept={UPLOAD_ACCEPT}
                  maxCount={1}
                  type="contract"
                  code={agreementId}
                  uploadBtnStyle={REUPLOAD_BTN_STYLE}
                />
              )}
            </Space>
          </Row>
        }
      >
        <Form labelCol={{ span: 5 }} wrapperCol={{ span: 19 }} colon={false}>
          <Form.Item
            label="审核状态"
            extra={isReviewing ? REVIEWING_TIP : undefined}
          >
            {statusText}
          </Form.Item>
          {isRejected && (
            <Form.Item label="驳回原因">{reason || '-'}</Form.Item>
          )}
          <Form.Item label="已上传附件">
            <Uploadfiles
              dataSource={fileList ?? []}
              maxCount={1}
              type="contract"
              code={agreementId}
              NoUploadButton
              disabledDel
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
import { Button, Row, Space, Modal, Form } from 'antd'
import moment from 'moment'
import Search from '@/components/SearchFilter'
import TableAntd from '@/components/TableAntd'
import Buttonbar from '@/components/ButtonBar'
import Uploadfiles from '@/modules/uploadfiles'
import { fileFile } from '@/modules/uploadfiles/hooks'
import {
  agreementSigningTypeEnum,
  agreementSignStatusEnum,
  agreementStatusEnum,
  agreementFileReviewStateEnum,
  AgreementSigningType,
  AgreementSignStatus,
  AgreementStatus,
  AgreementFileReviewState,
} from '@/global/enum/selfAgreement'
import { FrontSelfContractListVO } from '@/api/selfAgreementApi/response'
import { cn } from '@xhgj/xh-jsutils'
import { useSetupHook } from './hooks'
import styles from './styles.module.scss'

const UPLOAD_BTN_STYLE = {
  border: 'none',
  fontSize: '12px',
  color: '#3B44D3',
  backgroundColor: '#fff',
}
const UPLOAD_ACCEPT = '.pdf,.png,.jpg,.jpeg,.bmp,.gif'
// 审核详情弹窗「重新上传」按钮样式（复用 Uploadfiles 原生上传按钮）
const REUPLOAD_BTN_STYLE = {
  margin: 0,
  border: 'none',
  color: '#fff',
  backgroundColor: '#3B44D3',
}
const REVIEWING_TIP = '已提交H0审批，等待协议创建人审核。'

type TagColor = 'green' | 'gray' | 'yellow' | 'red' | 'blue'

// 通用状态标签（协议签署状态 / 协议状态）
const renderStatusTag = (text: string | undefined, color: TagColor) =>
  text ? (
    <span className={cn(styles['tag'], styles[color])}>{text}</span>
  ) : (
    <>-</>
  )

// 协议有效期（毫秒时间戳）→ 展示文本
const formatValidity = (record: FrontSelfContractListVO): string => {
  const { effectiveStart, effectiveEnd } = record
  if (!effectiveStart && !effectiveEnd) {
    return '-'
  }
  const start = effectiveStart
    ? moment(effectiveStart).format('YYYY-MM-DD')
    : ''
  const end = effectiveEnd ? moment(effectiveEnd).format('YYYY-MM-DD') : ''
  return `${start} ~ ${end}`
}

// 协议签署状态 → 标签颜色（未列出的默认灰）
const SIGN_STATUS_COLOR: Record<string, TagColor> = {
  双方已签署: 'green',
  我方已签署: 'yellow',
}
// 协议状态 → 标签颜色（未列出的默认灰）
const AGREEMENT_STATUS_COLOR: Record<string, TagColor> = {
  生效: 'green',
  失效: 'red',
}

export default function SelfAgreement() {
  const {
    state,
    searchRef,
    handlePaginationChange,
    handleTableChangeCheck,
    handleSearchClick,
    downloadFile,
    getAttachment,
    inTableUploadfile,
    fileListChange,
    cancelUpload,
    handleDownloadPendingFile,
  } = useSetupHook()
  const {
    dataSource,
    isTableLoading,
    paginationOption,
    downloadLoading,
    showList,
    fileList,
    fileStatus,
    reason,
    agreementId,
  } = state

  const reviewState = fileStatus?.fileReviewState
  const isReviewing = agreementFileReviewStateEnum.isValueInLabels(
    reviewState,
    ['审核中'],
  )
  const isRejected = agreementFileReviewStateEnum.isValueInLabels(reviewState, [
    '审核驳回',
  ])
  // 审核状态只读文案（label 即展示文案，直接取 enum）
  const statusText =
    agreementFileReviewStateEnum.getLabelByValue(
      reviewState as AgreementFileReviewState,
    ) ?? '-'

  // 协议待签署文件列：字段有值才展示按钮，文案按签订方式区分
  const renderPendingSignCell = (
    text: string,
    record: FrontSelfContractListVO,
  ) => {
    if (!text) {
      return '-'
    }
    // 全电子协议：跳转签署链接
    if (
      agreementSigningTypeEnum.isValueInLabels(record.signingType, [
        '全电子协议',
      ])
    ) {
      return (
        <Button type="link" onClick={() => window.open(text)}>
          签署链接
        </Button>
      )
    }
    // 我方电子对方纸质：拉取 contractPendingAttachment 链接内容并下载
    if (
      agreementSigningTypeEnum.isValueInLabels(record.signingType, [
        '我方电子对方纸质',
      ])
    ) {
      return (
        <Button
          type="link"
          onClick={() => handleDownloadPendingFile(text)}
        >
          下载待签署链接
        </Button>
      )
    }
    return '-'
  }

  // 协议附件列
  const renderAttachmentCell = (record: FrontSelfContractListVO) => {
    // 审核通过：展示归档附件文件名，预览/下载
    if (
      agreementFileReviewStateEnum.isValueInLabels(record.fileReviewState, [
        '审核通过',
      ])
    ) {
      const fileList = record.archiveFile
        ? [record.archiveFile as fileFile]
        : []
      return (
        <Uploadfiles
          dataSource={fileList}
          maxCount={1}
          type="contract"
          code="file"
          NoUploadButton
          disabledDel
        />
      )
    }
    // 审核中 / 审核驳回：标签，点击弹窗
    if (
      agreementFileReviewStateEnum.isValueInLabels(record.fileReviewState, [
        '审核中',
        '审核驳回',
      ])
    ) {
      const isRejectedRow = agreementFileReviewStateEnum.isValueInLabels(
        record.fileReviewState,
        ['审核驳回'],
      )
      return (
        <span
          className={cn(
            styles['tag'],
            styles['clickable'],
            isRejectedRow ? styles['red'] : styles['yellow'],
          )}
          onClick={() => getAttachment(record)}
        >
          {agreementFileReviewStateEnum.getLabelByValue(
            record.fileReviewState as AgreementFileReviewState,
          )}
        </span>
      )
    }
    // 未上传：显隐以后端 uploadSupported 为准
    if (record.uploadSupported) {
      return (
        <Uploadfiles
          title="上传"
          dataSource={[]}
          onChange={(e: fileFile[]) => inTableUploadfile({ e, record })}
          accept={UPLOAD_ACCEPT}
          maxCount={1}
          type="contract"
          code={record.id ?? ''}
          uploadBtnStyle={UPLOAD_BTN_STYLE}
          disabledDel
        />
      )
    }
    return '-'
  }

  const columns = [
    {
      key: 'contractNo',
      dataIndex: 'contractNo',
      title: '协议编号',
      width: 150,
    },
    {
      key: 'firstSigningGroupName',
      dataIndex: 'firstSigningGroupName',
      title: '甲方',
      width: 180,
    },
    {
      key: 'secondSigningSupplierName',
      dataIndex: 'secondSigningSupplierName',
      title: '乙方',
      width: 180,
    },
    {
      key: 'signingType',
      dataIndex: 'signingType',
      title: '签订方式',
      width: 130,
      render: (text: string) =>
        agreementSigningTypeEnum.getLabelByValue(
          text as AgreementSigningType,
        ) ??
        text ??
        '-',
    },
    {
      key: 'validity',
      dataIndex: 'effectiveStart',
      title: '协议有效期',
      width: 185,
      render: (text: number, record: FrontSelfContractListVO) =>
        formatValidity(record),
    },
    {
      key: 'untilContractExpirationDays',
      dataIndex: 'untilContractExpirationDays',
      title: '距离到期天数',
      width: 110,
    },
    {
      key: 'signatureStatus',
      dataIndex: 'signatureStatus',
      title: '协议签署状态',
      width: 120,
      render: (text: string) => {
        const label =
          agreementSignStatusEnum.getLabelByValue(
            text as AgreementSignStatus,
          ) ?? text
        return renderStatusTag(label, SIGN_STATUS_COLOR[label] ?? 'gray')
      },
    },
    {
      key: 'contractStatus',
      dataIndex: 'contractStatus',
      title: '协议状态',
      width: 90,
      render: (text: string) => {
        const label =
          agreementStatusEnum.getLabelByValue(text as AgreementStatus) ?? text
        return renderStatusTag(label, AGREEMENT_STATUS_COLOR[label] ?? 'gray')
      },
    },
    {
      key: 'contractPendingAttachment',
      dataIndex: 'contractPendingAttachment',
      title: '协议待签署文件',
      align: 'left',
      width: 120,
      render: renderPendingSignCell,
    },
    {
      key: 'archiveFile',
      dataIndex: 'archiveFile',
      title: '协议附件',
      width: 170,
      render: (text: unknown, record: FrontSelfContractListVO) =>
        renderAttachmentCell(record),
    },
  ]
  return (
    <div className={styles['container']}>
      <Search
        fieldsData={[
          'contractNo',
          'firstSigningGroupName',
          'agreementSigningType',
          'agreementSignStatus',
          'agreementStatus',
          'effectiveStart',
          'effectiveEnd',
          'untilContractExpirationDays',
        ]}
        onSearch={handleSearchClick}
        keyName="SELFAGREEMENT"
        ScreenKey="SELFAGREEMENT"
        currentRef={searchRef}
      />
      <Row justify="space-between" className={styles['info-row']}>
        <Space>
          <Buttonbar onRefresh={() => handlePaginationChange(1)}></Buttonbar>
        </Space>
        <Space>
          <Button
            size="middle"
            type="primary"
            loading={downloadLoading}
            onClick={downloadFile}
          >
            下载协议附件
          </Button>
        </Space>
      </Row>
      <TableAntd
        dataSource={dataSource}
        listColumn={columns as any[]}
        loading={isTableLoading}
        Y={340}
        X={1445}
        pagination={{
          ...paginationOption,
          onChange: handlePaginationChange,
        }}
        showSize
        onChangeCheck={handleTableChangeCheck}
      ></TableAntd>
      <Modal
        title="附件审核详情"
        open={showList}
        destroyOnClose
        onCancel={cancelUpload}
        footer={
          <Row justify="end">
            <Space>
              <Button onClick={cancelUpload}>关闭</Button>
              {isRejected && (
                <Uploadfiles
                  title="重新上传"
                  dataSource={[]}
                  onChange={fileListChange}
                  accept={UPLOAD_ACCEPT}
                  maxCount={1}
                  type="contract"
                  code={agreementId}
                  uploadBtnStyle={REUPLOAD_BTN_STYLE}
                />
              )}
            </Space>
          </Row>
        }
      >
        <Form labelCol={{ span: 5 }} wrapperCol={{ span: 19 }} colon={false}>
          <Form.Item
            label="审核状态"
            extra={isReviewing ? REVIEWING_TIP : undefined}
          >
            {statusText}
          </Form.Item>
          {isRejected && (
            <Form.Item label="驳回原因">{reason || '-'}</Form.Item>
          )}
          <Form.Item label="已上传附件">
            <Uploadfiles
              dataSource={fileList ?? []}
              maxCount={1}
              type="contract"
              code={agreementId}
              NoUploadButton
              disabledDel
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
